import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AUTO_INTERVAL_OPTIONS,
  CLIENT_PRESETS,
  getHealthMonitorEnvApiBase,
  normalizeBaseUrl,
} from './apiConfig';
import { loginForHealthMonitor } from './loginService';
import { runProbeSuite } from './apiTester';
import generatedPack from './generatedRoutes.json';

function escapeCsvCell(v) {
  const s = v === null || v === undefined ? '' : String(v);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export default function Dashboard() {
  const routes = useMemo(() => generatedPack.routes || [], []);
  const envBase = getHealthMonitorEnvApiBase();
  const [apiBase, setApiBase] = useState(
    envBase || CLIENT_PRESETS[0].apiBaseUrl
  );
  const [presetId, setPresetId] = useState(CLIENT_PRESETS[0].id);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [autoMs, setAutoMs] = useState(0);
  const [sequentialMode, setSequentialMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [suiteError, setSuiteError] = useState('');
  const [rows, setRows] = useState([]);
  const [progressLabel, setProgressLabel] = useState('');
  const [copiedUrlKey, setCopiedUrlKey] = useState(null);
  const abortRef = useRef(null);
  const routesRef = useRef(routes);

  const copyUrlToClipboard = useCallback(async (url, rowKey) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrlKey(rowKey);
      window.setTimeout(() => setCopiedUrlKey(null), 2000);
    } catch {}
  }, []);

  useEffect(() => {
    routesRef.current = routes;
  }, [routes]);

  useEffect(
    () => () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    },
    []
  );

  const protectedCount = useMemo(
    () => routes.filter((r) => r.auth === 'protected').length,
    [routes]
  );

  const executeSuite = useCallback(
    async (routeSubset) => {
      const list = routeSubset || routesRef.current;
      setSuiteError('');
      setLoading(true);
      setProgressLabel('');
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;
      try {
        const base = normalizeBaseUrl(apiBase);
        let tok = null;
        const hasProtected = list.some((r) => r.auth === 'protected');
        if (hasProtected) {
          const loginResult = await loginForHealthMonitor(
            base,
            username,
            password,
            signal
          );
          tok = loginResult.token;
        }
        const results = await runProbeSuite(
          list,
          base,
          tok,
          signal,
          (done, total) => {
            setProgressLabel(`${done} / ${total}`);
          },
          { sequential: sequentialMode }
        );
        setRows(results);
      } catch (e) {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') {
          return;
        }
        setSuiteError(e?.message || 'Suite failed');
      } finally {
        setLoading(false);
        setProgressLabel('');
      }
    },
    [apiBase, username, password, sequentialMode]
  );

  useEffect(() => {
    if (autoMs <= 0) return undefined;
    const id = window.setInterval(() => {
      executeSuite();
    }, autoMs);
    return () => window.clearInterval(id);
  }, [autoMs, executeSuite]);

  const summary = useMemo(() => {
    const total = rows.length;
    const tested = rows.filter((r) => r.passLabel !== 'SKIP');
    const passed = tested.filter((r) => r.pass === true).length;
    const failed = tested.filter(
      (r) => r.pass === false && r.passLabel !== 'SKIP'
    ).length;
    const skipped = rows.filter((r) => r.passLabel === 'SKIP').length;
    return { total, passed, failed, skipped };
  }, [rows]);

  const handlePresetChange = (e) => {
    const id = e.target.value;
    setPresetId(id);
    const p = CLIENT_PRESETS.find((x) => x.id === id);
    if (p) {
      setApiBase(p.apiBaseUrl);
    }
  };

  const exportCsv = () => {
    const header = [
      'API_URL',
      'Method',
      'Status_Code',
      'Response_Time_ms',
      'Pass_Fail',
      'Error_Message',
      'Auth_Class',
    ];
    const lines = [header.join(',')];
    rows.forEach((r) => {
      lines.push(
        [
          escapeCsvCell(r.url),
          escapeCsvCell(r.route?.method),
          escapeCsvCell(r.statusCode),
          escapeCsvCell(r.durationMs),
          escapeCsvCell(r.passLabel),
          escapeCsvCell(r.errorMessage),
          escapeCsvCell(r.authMode),
        ].join(',')
      );
    });
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `api-health-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const retestFailed = () => {
    const seen = new Set();
    const failedRoutes = [];
    rows.forEach((r) => {
      if (r.pass !== false || !r.route) return;
      const k = `${r.route.method}-${r.route.path}`;
      if (seen.has(k)) return;
      seen.add(k);
      failedRoutes.push(r.route);
    });
    if (!failedRoutes.length) return;
    executeSuite(failedRoutes);
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        API Health Monitor
      </Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'flex-end',
          }}
        >
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="client-preset-label">Client / API base</InputLabel>
            <Select
              labelId="client-preset-label"
              label="Client / API base"
              value={presetId}
              onChange={handlePresetChange}
            >
              {CLIENT_PRESETS.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
              <MenuItem value="custom">Custom URL</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="API base URL"
            value={apiBase}
            onChange={(e) => {
              setApiBase(e.target.value);
              setPresetId('custom');
            }}
            sx={{ flex: 1, minWidth: 280 }}
          />
          <TextField
            label="Username (incl. 3-letter prefix)"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ minWidth: 180 }}
          />
          <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="auto-int-label">Auto-run</InputLabel>
            <Select
              labelId="auto-int-label"
              label="Auto-run"
              value={autoMs}
              onChange={(e) => setAutoMs(Number(e.target.value))}
            >
              {AUTO_INTERVAL_OPTIONS.map((o) => (
                <MenuItem key={o.label} value={o.ms}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            sx={{ ml: 0, mr: 1, alignSelf: 'center' }}
            control={
              <Switch
                checked={sequentialMode}
                onChange={(e) => setSequentialMode(e.target.checked)}
                color="primary"
              />
            }
            label="Sequential"
          />
          <Button
            variant="contained"
            disabled={loading}
            onClick={() => executeSuite()}
          >
            Run all
          </Button>
          <Button variant="outlined" disabled={loading} onClick={retestFailed}>
            Retest failed
          </Button>
          <Button variant="outlined" onClick={exportCsv} disabled={!rows.length}>
            Export CSV
          </Button>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
          {routes.length} routes · ~{protectedCount} classified protected · sequential
          runs one API at a time (slower, gentler on server) · regenerate list with
          npm run generate:api-routes when Routes.php changes
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'warning.main' }}>
          POST probes send a small form field only. Some APIs may still run real logic
          and write to the database; this tool is a connectivity check, not a safe
          dry-run for every endpoint.
        </Typography>
        {suiteError ? (
          <Typography color="error" sx={{ mt: 1 }}>
            {suiteError}
          </Typography>
        ) : null}
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={28} />
            <Typography>{progressLabel || 'Running…'}</Typography>
          </Box>
        ) : null}
      </Paper>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 2,
          mb: 2,
        }}
      >
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Total rows
          </Typography>
          <Typography variant="h6">{summary.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Passed
          </Typography>
          <Typography variant="h6" color="success.main">
            {summary.passed}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Failed
          </Typography>
          <Typography variant="h6" color="error.main">
            {summary.failed}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Skipped
          </Typography>
          <Typography variant="h6">{summary.skipped}</Typography>
        </Paper>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>API URL</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Time (ms)</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Auth</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => (
              <TableRow key={`${r.url}-${idx}`}>
                <TableCell sx={{ maxWidth: 440, verticalAlign: 'top' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() =>
                        copyUrlToClipboard(r.url, `${r.url}-${idx}`)
                      }
                      sx={{
                        wordBreak: 'break-all',
                        textAlign: 'left',
                        cursor: 'pointer',
                        flex: '1 1 200px',
                        fontFamily: 'inherit',
                        fontSize: '0.875rem',
                      }}
                    >
                      {r.url}
                    </Link>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        copyUrlToClipboard(r.url, `${r.url}-${idx}-btn`)
                      }
                    >
                      {copiedUrlKey === `${r.url}-${idx}` ||
                      copiedUrlKey === `${r.url}-${idx}-btn`
                        ? 'Copied'
                        : 'Copy'}
                    </Button>
                  </Box>
                </TableCell>
                <TableCell>{r.route?.method}</TableCell>
                <TableCell>{r.statusCode ?? ''}</TableCell>
                <TableCell>{r.durationMs ?? ''}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={r.passLabel || (r.pass ? 'PASS' : 'FAIL')}
                    color={
                      r.passLabel === 'SKIP'
                        ? 'default'
                        : r.pass
                          ? 'success'
                          : 'error'
                    }
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 280 }}>{r.errorMessage}</TableCell>
                <TableCell>{r.authMode}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
